import type { ShortTaskOutput, TaskOutputWithNewlyCreated } from "@orsane/schema/tasks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useTaskQueryParams } from "@/components/task-table/TaskQueryParamsContext";
import { trpcClient, useTRPC } from "@/lib/trpc";
import { useTaskStatuses } from "@/queries/task-status-queries";

let forcedSortOrder = 1;

export function useChangeTaskProperty() {
    const trpc = useTRPC();
    const queryClient = useQueryClient();
    const queryParams = useTaskQueryParams();
    const { type, onMutationSuccess } = queryParams;

    return {
        // Forwarded from context — lets mutations notify an outer table (see SubTasks.tsx).
        // No-op when undefined (i.e. when used from the main table context).
        onMutationSuccess,
        change: async (taskId: number, updates: Record<string, any>): Promise<void> => {
            if (!type) {
                throw new Error("Type cannot be null when executing useChangeTaskProperty");
            }
            // Optimistically update task status in list view
            const listQueryKey = trpc.task.list.queryOptions(queryParams).queryKey;
            await queryClient.cancelQueries({ queryKey: listQueryKey });
            const previousTasks = queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(listQueryKey);
            if (previousTasks) {
                const updatedTasks = produce(previousTasks, (draft) => {
                    const taskIndex = draft.findIndex((t) => t.id === taskId);
                    if (taskIndex !== -1) {
                        Object.assign(draft[taskIndex], updates);
                    }
                });
                queryClient.setQueryData(listQueryKey, updatedTasks);
            }

            // Optimistically update task status in detail view
            const detailQueryKey = trpc.task.get.queryOptions({
                id: taskId,
            }).queryKey;
            await queryClient.cancelQueries({
                queryKey: detailQueryKey,
            });
            const previousTask = queryClient.getQueryData(detailQueryKey);
            if (previousTask) {
                queryClient.setQueryData(detailQueryKey, {
                    ...previousTask,
                    ...updates,
                });
            }
        },
        changeByFunction: async (
            taskId: number,
            modifier: (oldValue: TaskOutputWithNewlyCreated) => TaskOutputWithNewlyCreated
        ): Promise<void> => {
            if (!type) {
                throw new Error("Type cannot be null when executing useChangeTaskProperty");
            }

            // Optimistically update task in list view
            const listQueryKey = trpc.task.list.queryOptions(queryParams).queryKey;
            await queryClient.cancelQueries({ queryKey: listQueryKey });
            const previousTasks = queryClient.getQueryData<TaskOutputWithNewlyCreated[]>(listQueryKey);
            if (previousTasks) {
                const updatedTasks = produce(previousTasks, (draft) => {
                    const index = draft.findIndex((t) => t.id === taskId);
                    if (index !== -1) {
                        draft[index] = modifier(draft[index]) as TaskOutputWithNewlyCreated;
                    }
                });
                queryClient.setQueryData(listQueryKey, updatedTasks);
            }

            // Optimistically update task in detail view
            const detailQueryKey = trpc.task.get.queryOptions({
                id: taskId,
            }).queryKey;
            await queryClient.cancelQueries({
                queryKey: detailQueryKey,
            });
            const previousTask = queryClient.getQueryData(detailQueryKey);
            if (previousTask) {
                queryClient.setQueryData(detailQueryKey, modifier(previousTask as TaskOutputWithNewlyCreated));
            }
        },
    };
}

export function useCompleteTaskMutation() {
    const { data: statuses } = useTaskStatuses();
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    function findNextStatus(isCompleted: boolean) {
        if (!statuses) {
            throw new Error("useCompleteTaskMutation: statuses is required at this point.");
        }
        return isCompleted
            ? statuses!.find((status) => status.isCompleted)!
            : statuses!.find((status) => !status.isCompleted)!;
    }

    //const mutationOptions = trpc.task.updateStatus.mutationOptions();
    return useMutation({
        mutationFn: async ({ id, isCompleted }: { id: number; isCompleted: boolean }) => {
            const nextStatus = findNextStatus(isCompleted);

            await trpcClient.task.updateStatus.mutate({
                id,
                taskStatus: nextStatus,
            });
        },
        onMutate: async ({ id, isCompleted }) => {
            const nextStatus = findNextStatus(isCompleted);

            await changeTaskProperty(id, {
                taskStatusId: nextStatus.id,
                completedAt: nextStatus.isCompleted ? new Date() : null,
                taskStatus: nextStatus,
            });
        },
        onSuccess: () => {
            onMutationSuccess?.();
        },
    });
}

export function useDeleteTaskMutation() {
    const trpc = useTRPC();
    const queryParams = useTaskQueryParams();
    const { onMutationSuccess } = queryParams;

    const mutationOptions = trpc.task.delete.mutationOptions({
        onMutate: async ({ id }, context) => {
            // If we're in a tassk list, remove the task from the list
            const listQueryKey = trpc.task.list.queryOptions(queryParams).queryKey;
            await context.client.cancelQueries({ queryKey: listQueryKey });
            const previousTasks = context.client.getQueryData<TaskOutputWithNewlyCreated[]>(listQueryKey);
            if (previousTasks) {
                const updatedTasks = produce(previousTasks, (draft) => {
                    const index = draft.findIndex((task) => task.id === id);
                    if (index !== -1) {
                        draft.splice(index, 1);
                    }
                });
                context.client.setQueryData(listQueryKey, updatedTasks);
            }
        },
        onSuccess: () => {
            onMutationSuccess?.();
        },
    });

    return useMutation(mutationOptions);
}

export function useUpdateTaskPropertiesMutation() {
    const trpc = useTRPC();
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    const mutationOptions = trpc.task.updateProperties.mutationOptions({
        onMutate: async ({ id, updates }) => {
            await changeTaskProperty(id, updates);
            return {};
        },
        onSuccess: async (_, vars, _on, context) => {
            context.client.invalidateQueries(trpc.taskUpdates.list.queryOptions({ taskId: vars.id }));
            context.client.invalidateQueries(trpc.task.getActivityCount.queryOptions({ id: vars.id }));
            onMutationSuccess?.();
        },
    });

    return useMutation(mutationOptions);
}

export function useChangeTaskStatusMutation() {
    const trpc = useTRPC();
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    const mutationOptions = trpc.task.updateStatus.mutationOptions({
        onMutate: async ({ id, taskStatus }) => {
            await changeTaskProperty(id, {
                taskStatusId: taskStatus.id,
                taskStatus: taskStatus,
            });
            return {};
        },
        onSuccess: async (_, vars, _on, context) => {
            context.client.invalidateQueries(trpc.taskUpdates.list.queryOptions({ taskId: vars.id }));
            context.client.invalidateQueries(trpc.task.getActivityCount.queryOptions({ id: vars.id }));
            onMutationSuccess?.();
        },
    });
    return useMutation(mutationOptions);
}

export function useChangeTaskCustomAttributeMutation() {
    const trpc = useTRPC();
    const { changeByFunction: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    const mutationOptions = trpc.task.updateCustomAttribute.mutationOptions({
        onMutate: async ({ id, customAttributeId, value }) => {
            await changeTaskProperty(id, (task) => {
                const values = task.customAttributeValues || [];
                const fvIndex = values.findIndex((fv: any) => fv.id === customAttributeId);
                if (fvIndex !== -1) {
                    values[fvIndex].value = value;
                } else {
                    values.push({ id: customAttributeId, value });
                }
                return { ...task, customAttributeValues: values };
            });
            return {};
        },
        onSuccess: async (_, vars, _on, context) => {
            context.client.invalidateQueries(trpc.taskUpdates.list.queryOptions({ taskId: vars.id }));
            context.client.invalidateQueries(trpc.task.getActivityCount.queryOptions({ id: vars.id }));
            onMutationSuccess?.();
        },
    });

    return useMutation(mutationOptions);
}

export function useChangeTaskAssigneesMutation() {
    const trpc = useTRPC();
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    const mutationOptions = trpc.task.updateAssignees.mutationOptions({
        onMutate: async ({ id, assignees }) => {
            await changeTaskProperty(id, { assignees });
            return {};
        },
        onSuccess: () => {
            onMutationSuccess?.();
        },
    });

    return useMutation(mutationOptions);
}

export function useChangeTaskParentMutation() {
    const trpc = useTRPC();
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    const mutationOptions = trpc.task.updateParentTask.mutationOptions({
        onMutate: async ({ id, parentTask }: { id: number; parentTask: ShortTaskOutput | null }) => {
            await changeTaskProperty(id, { parentTask: parentTask });
            return {};
        },
        onSuccess: async (_, vars, _on, context) => {
            context.client.invalidateQueries(trpc.taskUpdates.list.queryOptions({ taskId: vars.id }));
            context.client.invalidateQueries({
                queryKey: trpc.task.getSubTasksCount.pathKey(),
            });
            onMutationSuccess?.();
        },
    });

    return useMutation(mutationOptions);
}

export function useUpdateListsOfTaskMutation() {
    const trpc = useTRPC();
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();

    const mutationOptions = trpc.task.updateLists.mutationOptions({
        onMutate: async ({ id, taskLists }) => {
            await changeTaskProperty(id, { taskLists: taskLists });
            return {};
        },
        onSuccess: () => {
            onMutationSuccess?.();
        },
    });

    return useMutation(mutationOptions);
}

/**
 * Custom to hook to insert a task after using the inline spreadsheet edit in the cell.
 */
export function useInsertTaskMutation() {
    const { change: changeTaskProperty, onMutationSuccess } = useChangeTaskProperty();
    const trpc = useTRPC();
    const myMutationOptions = trpc.task.insert.mutationOptions({
        onSuccess: async (responseData, task) => {
            // We also change the ID from the temp task to the real ID
            const dummyNegativeId = task.id!;
            await changeTaskProperty(dummyNegativeId, {
                id: responseData.id,
                number: responseData.number,
                isNewlyCreated: false,
                justCreated: true,
                forcedSortOrder: ++forcedSortOrder,
                title: task.title,
            });
            onMutationSuccess?.();
        },
    });
    return useMutation(myMutationOptions);
}
